import React, { useState } from "react";
import { AlertTriangle, MailX, MailCheck, Shield, ShieldCheck } from "lucide-react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";

const toneMap = {
  warning: {
    wrap: "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30",
    icon: "text-amber-600 dark:text-amber-300",
    title: "text-amber-900 dark:text-amber-200",
    text: "text-amber-800/90 dark:text-amber-200/85",
  },
  error: {
    wrap: "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30",
    icon: "text-rose-600 dark:text-rose-300",
    title: "text-rose-900 dark:text-rose-200",
    text: "text-rose-800/90 dark:text-rose-200/85",
  },
  info: {
    wrap: "bg-primary/5 border-primary/20",
    icon: "text-primary",
    title: "text-foreground",
    text: "text-muted-foreground",
  },
  success: {
    wrap: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30",
    icon: "text-emerald-600 dark:text-emerald-300",
    title: "text-emerald-900 dark:text-emerald-200",
    text: "text-emerald-800/90 dark:text-emerald-200/85",
  },
};

export default function AlertBanner({ alert, onChanged }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings, refresh: refreshSettings } = useSettings();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));

  if (!alert) return null;
  const tone = toneMap[alert.severity] || toneMap.warning;
  const Icon = Icons[alert.icon] || AlertTriangle;
  const changed = alert.changed_at ? fmtMDT(alert.changed_at) : null;

  const isEmailSafety = alert.kind === "email_safety";
  const allowlistCount = typeof alert.allowlist_count === "number"
    ? alert.allowlist_count
    : (settings?.qa_allowlist?.length ?? 0);

  const toggleEmailPaused = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      await api.put("/settings", { ...settings, email_paused: !settings.email_paused });
      await refreshSettings();
      toast({ title: settings.email_paused ? "Outbound email resumed" : "Outbound email paused" });
      onChanged?.();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const openAllowlist = () => navigate("/settings/super-admin");

  return (
    <div className={cn("rounded-xl border p-4 sm:p-5", tone.wrap)} data-testid="alert-banner">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={cn("shrink-0 mt-0.5", tone.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className={cn("font-semibold", tone.title)}>
                <AlertTriangle className="inline w-4 h-4 mr-1 -mt-0.5" />
                {alert.title}
              </div>
              {isEmailSafety && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-background/60 dark:bg-background/40 text-[11px] font-medium px-2 py-0.5 border border-border"
                  data-testid="alert-allowlist-count"
                >
                  <ShieldCheck className="w-3 h-3" /> {allowlistCount} QA {allowlistCount === 1 ? "inbox" : "inboxes"} allowed
                </span>
              )}
            </div>
            <div className={cn("mt-1 text-[13px] leading-relaxed", tone.text)}>
              {alert.message}
              {alert.changed_by && (
                <>
                  {" · "}last change by <span className="font-semibold">{alert.changed_by}</span>
                  {changed && <> at {changed}</>}
                </>
              )}
              {alert.reason && (
                <>
                  {" · "}reason: <em className="opacity-90">{alert.reason}</em>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEmailSafety ? (
            isAdmin && (
              <>
                <Button
                  variant="outline"
                  className="h-9 rounded-lg gap-1.5"
                  onClick={openAllowlist}
                  data-testid="alert-open-allowlist-btn"
                >
                  <Shield className="w-4 h-4" /> QA allowlist
                </Button>
                <Button
                  className="h-9 rounded-lg gap-1.5"
                  onClick={toggleEmailPaused}
                  disabled={busy || !settings}
                  data-testid="alert-toggle-email-btn"
                >
                  {settings?.email_paused ? (
                    <><MailCheck className="w-4 h-4" /> Resume email</>
                  ) : (
                    <><MailX className="w-4 h-4" /> Pause email</>
                  )}
                </Button>
              </>
            )
          ) : (
            <>
              {alert.action_label && (
                <Button variant="outline" className="h-9 rounded-lg gap-1.5">
                  <Shield className="w-4 h-4" /> {alert.action_label}
                </Button>
              )}
              {alert.secondary_action_label && (
                <Button className="h-9 rounded-lg gap-1.5">
                  <MailX className="w-4 h-4" /> {alert.secondary_action_label}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
